/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useEffect, useState } from 'react';
import { Accordion, AccordionItem, Select, SelectItem, TextInput } from '@carbon/react';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useApi } from 'contexts/ApiHandler';
// eslint-disable-next-line import/no-named-as-default
import TestToolParameter from './TestToolParameter';

export function TestToolParameters({ parameters, accepts, contentTypes, body, operationPath, operationMethod, onChange = () => {}, parameterState, options }) {
  const { t } = useTranslation();
  const { api } = useApi();
  const [contentType, setContentType] = useState(null);
  const [accept, setAccept] = useState(null);

  useEffect(() => {
    if (contentTypes && contentTypes.length > 0 && !contentType) {
      const newContentType = contentTypes.indexOf('application/json') !== -1 ? 'application/json' : contentTypes[0];
      setContentType(newContentType);
      onChange({ contentType: newContentType });
    }

    if (accepts && accepts.length > 0 && !accept) {
      const newAccept = accepts.indexOf('application/json') !== -1 ? 'application/json' : accepts[0];
      setAccept(newAccept);
      onChange({ accept: newAccept });
    }
  }, [contentTypes, accepts, onChange, accept, contentType]);

  const handleAcceptChange = (evt) => {
    setAccept(evt.target.value);
    onChange({ accept: evt.target.value });
  };

  const handleContentTypeChange = (evt) => {
    setContentType(evt.target.value);
    onChange({ contentType: evt.target.value });
  };

  const handleParamChange = (name, value) => {
    // Create a copy of the values object
    const parameterValues = JSON.parse(JSON.stringify(parameterState.parameterValues));
    if (!value && value !== '' && parameterValues[name].schema && parameterValues[name].schema.type !== 'boolean' && parameterValues[name].type !== 'boolean') {
      parameterValues[name].value = undefined;
    } else {
      parameterValues[name].value = value;
    }

    onChange({ parameterValues });
  };

  const getParametersByLocation = (parameters, location) => {
    const keepKeys = Object.keys(parameters).filter((paramKey) => {
      const param = parameters[paramKey];
      return param.in === location;
    });

    return keepKeys.map((key) => parameters[key]);
  };

  let queryParams;
  let pathParams;
  let formDataParams;
  let headerParams;
  let cookieParams = null;

  const currentAccept = parameterState.accept;
  const currentContentType = parameterState.contentType;
  const { parameterValues } = parameterState;

  if (parameters) {
    queryParams = getParametersByLocation(parameters, 'query');
    pathParams = getParametersByLocation(parameters, 'path');
    formDataParams = getParametersByLocation(parameters, 'formData');
    headerParams = getParametersByLocation(parameters, 'header');
    cookieParams = getParametersByLocation(parameters, 'cookie');
  }

  let maxDepth = 9;
  if (options && typeof options.apiMaxDepth !== 'undefined') {
    maxDepth = options.apiMaxDepth;
  }
  let exampleArrayItems = 3;
  if (options && typeof options.exampleArrayItems !== 'undefined') {
    exampleArrayItems = options.exampleArrayItems;
  }

  const hasHeaders = (accepts && accepts.length > 0) || (contentTypes && contentTypes.length > 0) || (headerParams && headerParams.length > 0);

  return (
    <div className={classNames('testToolSection', 'testToolParameterSection')}>
      <h3 className="labelText">
        {t('parameters')}
      </h3>
      <div className="testToolParameters">
        <Accordion align="start">
          {hasHeaders && (
            <AccordionItem className="parameter-header" title={t('header')} open>
              {accepts && accepts.length > 0 && (
                <div className="parameterOuter parameter-header-accept">
                  <div className="parameterName">{t('accept')}</div>
                  {accepts.length === 1 && (
                    <TextInput
                      labelText=""
                      id="acceptParameter"
                      name="acceptParameter"
                      type="input"
                      hideLabel
                      value={accepts[0]}
                      aria-label="Accept Parameter"
                      disabled
                    />
                  )}
                  {accepts.length > 1 && (
                    <Select value={currentAccept} onChange={handleAcceptChange} id="accept-select" hideLabel>
                      {accepts.map((param) => (
                        <SelectItem key={param} value={param} text={param} />
                      ))}
                    </Select>
                  )}
                </div>
              )}
              {contentTypes && contentTypes.length > 0 && (
                <div className="parameterOuter parameter-header-contentType">
                  <div className="parameterName">{t('content_type')}</div>
                  {contentTypes.length === 1 && (
                    <TextInput
                      labelText=""
                      id="contentTypeParameter"
                      name="contentTypeParameter"
                      type="input"
                      hideLabel
                      value={contentTypes[0]}
                      aria-label="Content-Type Parameter"
                      disabled
                    />
                  )}
                  {contentTypes.length > 1 && (
                    <Select value={currentContentType} onChange={handleContentTypeChange} id="content-type-select" hideLabel>
                      {contentTypes.map((param) => (
                        <SelectItem key={param} value={param} text={param} />
                      ))}
                    </Select>
                  )}
                </div>
              )}

              {parameters && headerParams && headerParams.length > 0 && (
                <div className="headerParameters">
                  {headerParams.map((parameter) => (
                    <TestToolParameter
                      key={parameter.name}
                      parameter={parameter}
                      name={parameter.name}
                      parameterValues={parameterValues}
                      openApi={api}
                      pathName={operationPath}
                      method={operationMethod}
                      contentTypeHeader={currentContentType}
                      onChange={handleParamChange}
                      maxDepth={maxDepth}
                      exampleArrayItems={exampleArrayItems}
                    />
                  ))}
                </div>
              )}
            </AccordionItem>
          )}

          {parameters && cookieParams && cookieParams.length > 0 && (
            <AccordionItem className="parameter-cookie" title={t('cookie')} open>
              <div className="cookieParameters">
                {cookieParams.map((parameter) => (
                  <TestToolParameter
                    key={parameter.name}
                    parameter={parameter}
                    name={parameter.name}
                    parameterValues={parameterValues}
                    openApi={api}
                    pathName={operationPath}
                    method={operationMethod}
                    contentTypeHeader={currentContentType}
                    onChange={handleParamChange}
                    maxDepth={maxDepth}
                    exampleArrayItems={exampleArrayItems}
                  />
                ))}
              </div>
            </AccordionItem>
          )}

          {parameters && pathParams && pathParams.length > 0 && (
            <AccordionItem className="parameter-path" title={t('path')} open>
              <div className="pathParameters">
                {pathParams.map((parameter) => (
                  <TestToolParameter
                    key={parameter.name}
                    parameter={parameter}
                    name={parameter.name}
                    parameterValues={parameterValues}
                    openApi={api}
                    pathName={operationPath}
                    method={operationMethod}
                    contentTypeHeader={currentContentType}
                    onChange={handleParamChange}
                    maxDepth={maxDepth}
                    exampleArrayItems={exampleArrayItems}
                  />
                ))}
              </div>
            </AccordionItem>
          )}

          {parameters && queryParams && queryParams.length > 0 && (
            <AccordionItem className="parameter-query" title={t('query')} open>
              <div className="queryParameters">
                {queryParams.map((parameter) => (
                  <TestToolParameter
                    key={parameter.name}
                    parameter={parameter}
                    name={parameter.name}
                    parameterValues={parameterValues}
                    openApi={api}
                    pathName={operationPath}
                    method={operationMethod}
                    contentTypeHeader={currentContentType}
                    onChange={handleParamChange}
                    maxDepth={maxDepth}
                    exampleArrayItems={exampleArrayItems}
                  />
                ))}
              </div>
            </AccordionItem>
          )}

          {parameters && formDataParams && formDataParams.length > 0 && (
            <AccordionItem className="parameter-form-data" title={t('form_data')} open>
              <div className="formDataParameters">
                {formDataParams.map((parameter) => (
                  <TestToolParameter
                    key={parameter.name}
                    parameter={parameter}
                    name={parameter.name}
                    parameterValues={parameterValues}
                    openApi={api}
                    pathName={operationPath}
                    method={operationMethod}
                    contentTypeHeader={currentContentType}
                    onChange={handleParamChange}
                    maxDepth={maxDepth}
                    exampleArrayItems={exampleArrayItems}
                  />
                ))}
              </div>
            </AccordionItem>
          )}

          {body && (
            <AccordionItem className="parameter-body" title={t('body')} open>
              <div className="bodyParam">
                <TestToolParameter
                  key={body.name}
                  parameter={body}
                  name={body.name}
                  isBody
                  parameterValues={parameterValues}
                  openApi={api}
                  pathName={operationPath}
                  method={operationMethod}
                  contentTypeHeader={currentContentType}
                  onChange={handleParamChange}
                  maxDepth={maxDepth}
                  exampleArrayItems={exampleArrayItems}
                />
              </div>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  );
}

TestToolParameters.propTypes = {
  parameters: PropTypes.object,
  accepts: PropTypes.array,
  contentTypes: PropTypes.array,
  bodyParam: PropTypes.object,
  parameterState: PropTypes.object,
  onChange: PropTypes.func
};

export default withTranslation()(TestToolParameters);
